import { useState } from "react"
import { useTranslation } from "react-i18next"
import styles from "./associated-list.module.css"
import ArrowDown from "@/assets/icons/misc/arrow-down.tsx"

interface AssociatedListProps<T> {
  items: T[]
  onItemsChange: (items: T[]) => void
  renderItem: (item: T, onDelete: (id: T) => void) => JSX.Element
  seeMoreText: string
  hideMoreText: string
}

const AssociatedList = <T extends string>({
  items,
  onItemsChange,
  renderItem,
  seeMoreText,
  hideMoreText,
}: AssociatedListProps<T>) => {
  const { t } = useTranslation()
  const [listLimit, setListLimit] = useState(3)

  const expandList = () => {
    setListLimit(items.length)
  }

  const collapseList = () => {
    setListLimit(3)
  }

  const handleItemDeletion = (itemId: T) => {
    const updatedItems = items.filter((id) => id !== itemId)
    onItemsChange(updatedItems)
  }

  return (
    <div className={styles["associated-list"]}>
      {items
        .slice(0, listLimit)
        .map((item) => renderItem(item, handleItemDeletion))}
      {items.length > listLimit && (
        <div
          className={"flex w-full items-center justify-center gap-2"}
          onClick={expandList}
        >
          <i className={styles["more-items"]}>{t(seeMoreText)}</i>
          <div className={"scale-125"}>
            <ArrowDown />
          </div>
        </div>
      )}
      {items.length === listLimit && items.length > 3 && (
        <div
          className={"flex w-full items-center justify-center gap-2"}
          onClick={collapseList}
        >
          <i className={styles["more-items"]}>{t(hideMoreText)}</i>
          <div className={"scale-125"}>
            <ArrowDown isDown={false} />
          </div>
        </div>
      )}
    </div>
  )
}

export default AssociatedList
